import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmDialog,
  Group,
  BottomDrawer,
  PageHeader,
  Paper,
  ResourceLayout,
  Select,
  Stack,
  TabView,
  Text,
  TextInput,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useBroadcastOrgRefreshMutation,
  useCreateOrgMutation,
  useDeleteOrgMutation,
  useGetOrgsQuery,
  useGetTenantsQuery,
  useUpdateOrgStatusMutation,
  type Org,
} from '../api/adminApi';

const ORG_TABS = [
  { value: 'Invited', label: 'Invited' },
  { value: 'Registration', label: 'Registration' },
  { value: 'Active', label: 'Active' },
  { value: 'Deactive', label: 'Deactive' },
] as const;

type OrgTab = (typeof ORG_TABS)[number]['value'];

export function OrgsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<OrgTab>('Invited');
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });
  const [pendingAction, setPendingAction] = useState<{
    org: Org;
    nextStatus: string;
    label: string;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Org | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ tenantId: '', name: '', phone: '' });

  const { data, isLoading, isFetching } = useGetOrgsQuery({
    status: activeTab,
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const { data: tenantsData } = useGetTenantsQuery({ page: 1, pageLimit: 100 });
  const [updateOrgStatus, updateState] = useUpdateOrgStatusMutation();
  const [broadcastRefresh, broadcastState] = useBroadcastOrgRefreshMutation();
  const [createOrg, createState] = useCreateOrgMutation();
  const [deleteOrg, deleteState] = useDeleteOrgMutation();

  const list = data?.orgs;

  function openDetails(orgId: string) {
    navigate(`/orgs/${orgId}`);
  }

  function renderActions(row: Org) {
    return (
      <Group gap="xs" onClick={(e) => e.stopPropagation()}>
        {activeTab === 'Invited' && (
          <>
            <Button
              size="xs"
              onClick={() =>
                setPendingAction({ org: row, nextStatus: 'Registration', label: 'Accept' })
              }
            >
              Accept
            </Button>
            <Button
              size="xs"
              variant="default"
              color="red"
              onClick={() =>
                setPendingAction({ org: row, nextStatus: 'Deactive', label: 'Decline' })
              }
            >
              Decline
            </Button>
          </>
        )}
        {activeTab === 'Registration' && (
          <>
            <Button
              size="xs"
              onClick={() =>
                setPendingAction({ org: row, nextStatus: 'Active', label: 'Activate' })
              }
            >
              Activate
            </Button>
            <Button size="xs" variant="default" onClick={() => openDetails(row.id)}>
              Details
            </Button>
          </>
        )}
        {activeTab === 'Deactive' && (
          <Button
            size="xs"
            onClick={() =>
              setPendingAction({ org: row, nextStatus: 'Active', label: 'Reactivate' })
            }
          >
            Reactivate
          </Button>
        )}
        {activeTab === 'Active' && (
          <>
            <Button size="xs" variant="default" onClick={() => openDetails(row.id)}>
              Details
            </Button>
            <Button
              size="xs"
              variant="default"
              color="red"
              onClick={() =>
                setPendingAction({ org: row, nextStatus: 'Deactive', label: 'Deactivate' })
              }
            >
              Deactivate
            </Button>
            <Button
              size="xs"
              variant="default"
              loading={broadcastState.isLoading}
              onClick={() => void broadcastRefresh({ orgId: row.id })}
            >
              Refresh
            </Button>
          </>
        )}
        <Button size="xs" color="red" variant="light" onClick={() => setPendingDelete(row)}>
          Delete
        </Button>
      </Group>
    );
  }

  async function handleCreate() {
    await createOrg({
      tenantId: form.tenantId,
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      status: 'Invited',
    }).unwrap();
    setCreateOpen(false);
    setForm({ tenantId: '', name: '', phone: '' });
    setActiveTab('Invited');
  }

  function applyTab(value: string) {
    setActiveTab(value as OrgTab);
    setQuery((q) => ({ ...q, page: 1, search: '' }));
  }

  const filterContent = (
    <Stack gap="xs">
      {ORG_TABS.map((tab) => (
        <Button
          key={tab.value}
          variant={activeTab === tab.value ? 'filled' : 'default'}
          onClick={() => applyTab(tab.value)}
        >
          {tab.label}
        </Button>
      ))}
    </Stack>
  );

  const resourceLayout = (
    <ResourceLayout
      data={list}
      isLoading={isLoading}
      isFetching={isFetching}
      onQueryChange={setQuery}
      getRowId={(row) => row.id}
      searchable
      searchPlaceholder="Search organizations..."
      emptyTitle={`No ${activeTab.toLowerCase()} organizations`}
      emptyDescription="Organizations in this status will appear here."
      addButton={
        <Button onClick={() => setCreateOpen(true)}>Invite organization</Button>
      }
      filterActiveCount={1}
      filterOnlyCompact
      filterContent={filterContent}
      columns={[
        { key: 'id', header: 'ID', render: (row) => row.id },
        { key: 'name', header: 'Organization', render: (row) => row.name },
        { key: 'tenantId', header: 'Tenant ID', render: (row) => row.tenantId },
        { key: 'phone', header: 'Phone', render: (row) => row.phone ?? '—' },
        { key: 'uniqueId', header: 'Unique ID', render: (row) => row.uniqueId ?? '—' },
        {
          key: 'status',
          header: 'Status',
          render: (row) => <Badge>{row.status}</Badge>,
        },
        { key: 'actions', header: 'Actions', render: (row) => renderActions(row) },
      ]}
      renderCard={(row: Org) => (
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text fw={600}>{row.name}</Text>
                <Text size="sm" c="dimmed">
                  ID {row.id} · Tenant {row.tenantId} · {row.uniqueId || '—'} ·{' '}
                  {row.phone || 'No phone'}
                </Text>
              </Stack>
              <Badge>{row.status}</Badge>
            </Group>
            {renderActions(row)}
          </Stack>
        </Paper>
      )}
    />
  );

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Review and manage organizations by lifecycle status."
      />

      <TabView
        visibleFrom="md"
        value={activeTab}
        onChange={applyTab}
        tabs={ORG_TABS.map((tab) => ({
          value: tab.value,
          title: tab.label,
          content: resourceLayout,
        }))}
      />

      <Stack hiddenFrom="md">{resourceLayout}</Stack>

      <ConfirmDialog
        opened={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        onConfirm={async () => {
          if (!pendingAction) return;
          await updateOrgStatus({
            orgId: pendingAction.org.id,
            status: pendingAction.nextStatus,
          }).unwrap();
          setPendingAction(null);
        }}
        title={pendingAction ? `${pendingAction.label} organization` : 'Confirm'}
        message={
          pendingAction
            ? `Are you sure you want to ${pendingAction.label.toLowerCase()} "${pendingAction.org.name}"?`
            : ''
        }
        confirmLabel={pendingAction?.label ?? 'Confirm'}
        loading={updateState.isLoading}
        danger={
          pendingAction?.label === 'Decline' || pendingAction?.label === 'Deactivate'
        }
      />

      <ConfirmDialog
        opened={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          await deleteOrg({ orgId: pendingDelete.id }).unwrap();
          setPendingDelete(null);
        }}
        title="Delete organization"
        message={
          pendingDelete ? `Delete "${pendingDelete.name}"? This cannot be undone easily.` : ''
        }
        confirmLabel="Delete"
        loading={deleteState.isLoading}
        danger
      />

      <BottomDrawer opened={createOpen} onClose={() => setCreateOpen(false)} title="Invite organization">
        <Stack gap="md">
          <Select
            label="Tenant"
            placeholder="Select tenant"
            value={form.tenantId}
            onChange={(value) => setForm((f) => ({ ...f, tenantId: value || '' }))}
            data={(tenantsData?.tenants.rows ?? []).map((t) => ({
              value: t.id,
              label: t.name,
            }))}
            required
          />
          <TextInput
            label="Organization name"
            value={form.name}
            onChange={(e) => {
              const name = e.currentTarget.value;
              setForm((f) => ({ ...f, name }));
            }}
            required
          />
          <TextInput
            label="Phone"
            value={form.phone}
            onChange={(e) => {
              const phone = e.currentTarget.value;
              setForm((f) => ({ ...f, phone }));
            }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={createState.isLoading}
              disabled={!form.tenantId || !form.name.trim()}
              onClick={() => void handleCreate()}
            >
              Invite
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </>
  );
}
